import { useEffect, useMemo, useState } from "react";
import { FileText } from "lucide-react";
import Accordion from "../../components/Accordion";
import Alert from "../../components/Alert";
import Toggle from "../../components/Toggle";
import Rt3199 from "./Rt3199";
import LiveLineTester from "./LiveLineTester";
import EngineeringTrains from "./EngineeringTrains";
import OtpAndTrolley from "./OtpAndTrolley";
import RecordOfEquipment from "./RecordOfEquipment";
import RecordOfMovement from "./RecordOfMovement";

const optionalForms = [
  { key: "live_line_tester", title: "Live line tester", reference: "NR/L3/ELP/3091" },
  { key: "eng_train", title: "ES VMF for engineering trains & OTM", reference: "NR/L3/OPS/084F04" },
  { key: "otp_trolley", title: "ES VMF for OTP & Trolley", reference: "NR/L3/OPS/084F05" },
  { key: "equipment_placement_removal", title: "PSE process record of equipment placement / removal", reference: "NR/L3/OPS/255FC" },
  { key: "record_of_movement", title: "ES Record of movements", reference: "NR/L3/OPS/255FA" },
];

const Forms = (props) => {
  const [allLines, setAllLines] = useState([]);
  const [lineElrs, setLineElrs] = useState({});
  const [elrBoundaries, setElrBoundaries] = useState({});
  const [newlySelectedForm, setNewlySelectedForm] = useState(null);
  const packData = props.values;
  const locationData = packData?.location || {};
  const possessionData = useMemo(() => packData?.possessions || [], [packData?.possessions]);
  const allForms = props.allForms || {};
  const esUsers = packData?.users?.esusers || [];
  const cossUsers = packData?.users?.cossusers || [];
  const cossXusers = packData?.users?.cossXusers || [];
  const allCossUsers = [...cossUsers, ...cossXusers];
  const conversionUnit = props.conversionUnit;

  useEffect(() => {
    const linesArray = possessionData.flatMap((possession) => possession?.worksites?.flatMap((worksite) => worksite?.lines_at_site?.filter((line) => line?.name).map((line) => ({ line_reference: line.line_reference, name: line.name })) || []) || []);
    const references = {};
    const boundaries = {};
    possessionData.forEach((possession) => possession?.worksites?.forEach((worksite) => worksite?.lines_at_site?.forEach((line) => {
      if (line.line_reference) references[line.line_reference] = [...new Set([line.from_elr?.elr, line.to_elr?.elr].filter(Boolean))];
      [line.from_elr, line.to_elr].forEach((elr) => {
        if (!elr?.elr || !elr.boundaries?.start || !elr.boundaries?.end) return;
        boundaries[elr.elr] = {
          milesYards: `Boundary from: ${elr.boundaries.start.milesYards} to: ${elr.boundaries.end.milesYards}`,
          milesChains: `Boundary from: ${elr.boundaries.start.milesChains} to: ${elr.boundaries.end.milesChains}`,
          kilometersMeters: `Boundary from: ${elr.boundaries.start.kilometersMeters} to: ${elr.boundaries.end.kilometersMeters}`,
          start: elr.boundaries.start,
          end: elr.boundaries.end,
        };
      });
    })));
    setAllLines(linesArray);
    setLineElrs(references);
    setElrBoundaries(boundaries);
  }, [possessionData]);

  const setFormActive = (key, active) => {
    props.handleInputChange(`forms.${key}`, { ...(allForms[key] || {}), active: active ? 1 : 0 });
    setNewlySelectedForm(active ? key : null);
  };
  const activeForms = optionalForms.filter(({ key }) => Number(allForms[key]?.active) === 1);
  const lineRecords = possessionData.flatMap((possession, possessionIndex) => possession?.worksites?.flatMap((worksite, worksiteIndex) => worksite?.lines_at_site?.map((line, lineIndex) => ({ line, possession, possessionIndex, worksite, worksiteIndex, lineIndex })) || []) || []);

  const renderOptionalForm = (key) => {
    if (key === "live_line_tester") return <LiveLineTester errors={props.errors} formData={allForms[key]} handleAddFormRow={props.handleAddFormRow} handleInputChange={props.handleInputChange} handleRemoveFormRow={props.handleRemoveFormRow} locationData={locationData} />;
    if (key === "eng_train") return <EngineeringTrains errors={props.errors} formData={allForms[key]} handleAddFormRow={props.handleAddFormRow} handleInputChange={props.handleInputChange} handleRemoveFormRow={props.handleRemoveFormRow} locationData={locationData} />;
    if (key === "otp_trolley") return <OtpAndTrolley allCossUsers={allCossUsers} errors={props.errors} allLines={allLines} formData={allForms[key]} handleAddFormRow={props.handleAddFormRow} handleInputChange={props.handleInputChange} handleRemoveFormRow={props.handleRemoveFormRow} locationData={locationData} />;
    if (key === "equipment_placement_removal") return <RecordOfEquipment allCossUsers={allCossUsers} errors={props.errors} allEsUsers={esUsers} formData={allForms[key]} handleAddFormRow={props.handleAddFormRow} handleInputChange={props.handleInputChange} handleRemoveFormRow={props.handleRemoveFormRow} locationData={locationData} />;
    return <RecordOfMovement allCossUsers={allCossUsers} errors={props.errors} allEsUsers={esUsers} allLines={allLines} conversionUnit={conversionUnit} elrBoundaries={elrBoundaries} formData={allForms[key]} handleAddFormRow={props.handleAddFormRow} handleInputChange={props.handleInputChange} handleRemoveFormRow={props.handleRemoveFormRow} lineElrs={lineElrs} locationData={locationData} />;
  };

  return (
    <div className="wsm-forms-step">
      {props.errors?.["data.forms"] && <Alert title="Check required records" type="error">{props.errors["data.forms"][0]}</Alert>}

      <section className="wsm-pack-form-section">
        <div className="wsm-pack-form-section__heading">
          <div><h3>Required line records</h3><p>An RT3199 record is included for every line configured in the location step.</p></div>
        </div>
        {lineRecords.length === 0 ? <p className="wsm-pack-form-section__empty">Add a named line in the location step to create its RT3199 record.</p> : (
          <Accordion>
            {lineRecords.map(({ line, possession, possessionIndex, worksite, worksiteIndex, lineIndex }, index) => (
              <Accordion.Item defaultOpen={index === 0} description={`Possession: ${possession.possession_ref || possession.possession_no || locationData.possession_no || possessionIndex + 1} - Worksite: ${worksite.worksite_ref || worksiteIndex + 1} - RT3199`} key={`rt3199-${line.line_reference || `${possessionIndex}-${worksiteIndex}-${lineIndex}`}`} title={line.name || `Line ${lineIndex + 1}`}>
                <Rt3199 conversionUnit={conversionUnit} esUserData={props.allUserData?.esusers || []} handleInputChange={props.handleInputChange} lineData={line} lineIndex={lineIndex} locationData={locationData} possessionData={possession} possessionIndex={possessionIndex} rt3199Data={line.rt3199} worksiteData={worksite} worksiteIndex={worksiteIndex} />
              </Accordion.Item>
            ))}
          </Accordion>
        )}
      </section>

      <section className="wsm-pack-form-section wsm-forms-step__selector">
        <div className="wsm-pack-form-section__heading">
          <div><h3>Additional records</h3><p>Select the operational records that are required for this pack.</p></div>
          <span className="wsm-forms-step__count" aria-live="polite">{activeForms.length} selected</span>
        </div>
        <div className="wsm-forms-step__options">
          {optionalForms.map(({ key, title, reference }) => <Toggle
            checked={Number(allForms[key]?.active) === 1}
            className="wsm-forms-step__option"
            id={`form-${key}`}
            key={key}
            label={<span className="wsm-forms-step__option-label"><span className="wsm-forms-step__option-icon"><FileText aria-hidden="true" /></span><span className="wsm-forms-step__option-text"><strong>{title}</strong><small>{reference}</small></span></span>}
            onChange={(event) => setFormActive(key, event.target.checked)}
          />)}
        </div>
        {activeForms.length > 0 && <div className="wsm-forms-step__selected">
          <div className="wsm-forms-step__selected-heading"><h4>Record details</h4><p>Complete each selected operational record.</p></div>
          <Accordion>
            {activeForms.map(({ key, title, reference }, index) => <Accordion.Item defaultOpen={index === 0 || newlySelectedForm === key} description={reference} key={key} title={title}>{renderOptionalForm(key)}</Accordion.Item>)}
          </Accordion>
        </div>}
      </section>
    </div>
  );
};

export default Forms;
